import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { COOKIES_VERSION } from "@/lib/legalVersions";

export type CookieChoices = {
  essential: true; // sempre true
  analytics: boolean;
  functional: boolean;
};

export type CookieConsentRecord = {
  version: string;
  timestamp: string;
  choices: CookieChoices;
};

const STORAGE_KEY = "cookie_consent_v1";

export function getCookieConsent(): CookieConsentRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsentRecord;
    if (parsed.version !== COOKIES_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function useCookieConsent() {
  const [consent, setConsent] = useState<CookieConsentRecord | null>(() => getCookieConsent());
  const [open, setOpen] = useState<boolean>(() => !getCookieConsent());

  // Permite reabrir o banner via evento global ("Gerenciar cookies" no rodapé)
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-cookie-consent", handler);
    return () => window.removeEventListener("open-cookie-consent", handler);
  }, []);

  const save = useCallback(async (choices: Omit<CookieChoices, "essential">) => {
    const record: CookieConsentRecord = {
      version: COOKIES_VERSION,
      timestamp: new Date().toISOString(),
      choices: { essential: true, ...choices },
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    setConsent(record);
    setOpen(false);

    // Se logado, registra também no servidor como prova legal
    const { data } = await supabase.auth.getUser();
    if (data.user) {
      supabase.functions.invoke("record-consent", {
        body: {
          consent_type: "cookies",
          version: COOKIES_VERSION,
          accepted: true,
          choices: record.choices,
        },
      }).catch(() => {});
    }

    // Notifica outros componentes (ex: para injetar/remover GA)
    window.dispatchEvent(new CustomEvent("cookie-consent-changed", { detail: record }));
  }, []);

  return { consent, open, setOpen, save };
}

export function openCookieSettings() {
  window.dispatchEvent(new Event("open-cookie-consent"));
}